import { Box, HStack, Link, Text, VStack } from '@chakra-ui/react';
import { ExternalLink } from 'lucide-react';
import type React from 'react';

import { NumberInput } from '~/components/inputs/NumberInput';
import type { BondInterestRateSettings } from '~/types/app-settings';
import { BOND_RATE_CONSTRAINTS } from '~/types/app-settings';

export interface BondInterestRateInputsProps {
  values: BondInterestRateSettings;
  onChange: (field: keyof BondInterestRateSettings, value: string) => void;
}

export const BondInterestRateInputs: React.FC<BondInterestRateInputsProps> = ({
  values,
  onChange,
}) => {
  // Calculate example escalation
  const startRateNum =
    typeof values.startRate === 'string' ? parseFloat(values.startRate) : values.startRate;
  const incrementNum =
    typeof values.incrementRate === 'string'
      ? parseFloat(values.incrementRate)
      : values.incrementRate;
  const maxRateNum =
    typeof values.maxRate === 'string' ? parseFloat(values.maxRate) : values.maxRate;
  const secondPaymentRate =
    Number.isNaN(startRateNum) || Number.isNaN(incrementNum) ? 0 : startRateNum + incrementNum;

  return (
    <VStack align="stretch" gap={4}>
      <HStack justify="space-between" align="center">
        <Text fontSize="sm" color="gray.600">
          Bond interest rate settings for this project. These values are inherited from app settings
          but can be overridden.
        </Text>
        <Link href="/settings" fontSize="xs" color="blue.600" textDecoration="underline">
          <HStack gap={1}>
            <Text>Edit App Baseline</Text>
            <ExternalLink size={12} />
          </HStack>
        </Link>
      </HStack>

      <HStack gap={4} align="flex-start">
        {/* Start Rate */}
        <Box flex={1}>
          <NumberInput
            label="Starting Rate (%)"
            value={values.startRate.toString()}
            onChange={(val) => onChange('startRate', val)}
            min={BOND_RATE_CONSTRAINTS.MIN_RATE}
            max={BOND_RATE_CONSTRAINTS.MAX_RATE}
            step={BOND_RATE_CONSTRAINTS.STEP}
            precision={BOND_RATE_CONSTRAINTS.DECIMAL_PLACES}
            isRequired
          />
        </Box>

        {/* Increment Rate */}
        <Box flex={1}>
          <NumberInput
            label="Increment per Payment (%)"
            value={values.incrementRate.toString()}
            onChange={(val) => onChange('incrementRate', val)}
            min={0}
            max={1}
            step={BOND_RATE_CONSTRAINTS.STEP}
            precision={BOND_RATE_CONSTRAINTS.DECIMAL_PLACES}
            isRequired
          />
        </Box>

        {/* Max Rate */}
        <Box flex={1}>
          <NumberInput
            label="Maximum Rate (%)"
            value={values.maxRate.toString()}
            onChange={(val) => onChange('maxRate', val)}
            min={BOND_RATE_CONSTRAINTS.MIN_RATE}
            max={BOND_RATE_CONSTRAINTS.MAX_RATE}
            step={BOND_RATE_CONSTRAINTS.STEP}
            precision={BOND_RATE_CONSTRAINTS.DECIMAL_PLACES}
            isRequired
          />
        </Box>
      </HStack>

      {/* Example */}
      <Box p={2} bg="blue.50" borderRadius="md">
        <Text fontSize="xs" color="gray.700">
          <strong>Example:</strong> Payment 1 ={' '}
          {Number.isNaN(startRateNum) ? '?' : startRateNum.toFixed(3)}%, Payment 2 ={' '}
          {Number.isNaN(secondPaymentRate) ? '?' : secondPaymentRate.toFixed(3)}%, ..., Max ={' '}
          {Number.isNaN(maxRateNum) ? '?' : maxRateNum.toFixed(3)}%
        </Text>
      </Box>
    </VStack>
  );
};
